import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getVehicleMakeSuggestions, getVehicleModelSuggestions, VehicleMake, VehicleModel } from '../../services/vehicleApi';

interface VehicleSearchProps {
  onVehicleSelect: (vehicle: { make: VehicleMake; model: VehicleModel }) => void;
  onDropdownStateChange?: (isOpen: boolean) => void;
  className?: string;
}

interface VehicleSuggestion {
  type: 'make' | 'model';
  data: VehicleMake | VehicleModel;
  context?: string;
}

const ModernVehicleSearch: React.FC<VehicleSearchProps> = ({ onVehicleSelect, onDropdownStateChange, className = '' }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<VehicleSuggestion[]>([]);
  const [selectedMake, setSelectedMake] = useState<VehicleMake | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchStep, setSearchStep] = useState<'make' | 'model' | 'complete'>('make');
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const [logoErrors, setLogoErrors] = useState<Set<string>>(new Set());

  // Get manufacturer logo URL from jsdelivr CDN (cardog-icons package)
  const getManufacturerLogoUrl = (brandName: string) => {
    // Map brand names to their proper capitalized format as they appear in the CDN
    const brandMap: Record<string, string> = {
      'toyota': 'Toyota',
      'bmw': 'BMW',
      'mercedes-benz': 'MB',
      'mercedes': 'MB',
      'audi': 'Audi',
      'honda': 'Honda',
      'ford': 'Ford',
      'volkswagen': 'Volkswagen',
      'nissan': 'Nissan',
      'hyundai': 'Hyundai',
      'kia': 'Kia',
      'mazda': 'Mazda',
      'subaru': 'Subaru',
      'lexus': 'Lexus',
      'volvo': 'Volvo',
      'jaguar': 'Jaguar',
      'land rover': 'Landrover',
      'porsche': 'Porsche',
      'ferrari': 'Ferrari',
      'lamborghini': 'Lamborghini',
      'maserati': 'Maserati',
      'tesla': 'Tesla',
      'chevrolet': 'Chevrolet',
      'dodge': 'Dodge',
      'jeep': 'Jeep',
      'ram': 'RAM',
      'gmc': 'GMC',
      'cadillac': 'Cadillac',
      'buick': 'Buick',
      'chrysler': 'Chrysler',
      'lincoln': 'Lincoln',
      'acura': 'Acura',
      'infiniti': 'Infiniti',
      'genesis': 'Genesis',
      'alfa romeo': 'Alfa Romeo',
      'fiat': 'Fiat',
      'mini': 'Mini',
      'rolls-royce': 'Rolls Royce',
      'rolls royce': 'Rolls Royce',
      'bentley': 'Bentley',
      'aston martin': 'Aston Martin',
      'mclaren': 'McLaren',
      'lotus': 'Lotus',
      'polestar': 'Polestar',
      'lucid': 'Lucid',
      'byd': 'BYD',
      'mitsubishi': 'Mitsubishi',
      'hummer': 'Hummer',
      'vinfast': 'Vinfast'
    };
    
    const normalizedBrand = brandName.toLowerCase().trim();
    const properName = brandMap[normalizedBrand] || brandName;
    
    // Files are named like "Toyota Icon.svg" in the optimized folder
    // URL encode the spaces
    return `https://cdn.jsdelivr.net/npm/@cardog-icons/core@1.0.1/optimized/${encodeURIComponent(properName + ' Icon.svg')}`;
  };

  // Handle logo load errors
  const handleLogoError = (brandName: string) => {
    setLogoErrors(prev => new Set(prev).add(brandName));
  };

  // Render manufacturer logo or fallback
  const ManufacturerLogo = ({ brandName }: { brandName: string }) => {
    if (logoErrors.has(brandName)) {
      return (
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    }

    return (
      <img 
        src={getManufacturerLogoUrl(brandName)}
        alt={`${brandName} logo`}
        className="w-full h-full object-contain"
        onError={() => handleLogoError(brandName)}
      />
    );
  };

  // Get popularity score for visual ranking
  const getPopularityScore = (item: VehicleMake | VehicleModel) => {
    if ('popular' in item) return item.popular ? 100 : 50;
    return 70; // Default score for models
  };

  // Debounced search function
  const performSearch = async (searchQuery: string) => {
    if (searchQuery.length === 0) {
      setSuggestions([]);
      setIsOpen(false);
      onDropdownStateChange?.(false);
      return;
    }

    setIsLoading(true);
    try {
      const results: VehicleSuggestion[] = [];
      
      if (searchStep === 'make' || !selectedMake) {
        // Search for makes
        const makes = await getVehicleMakeSuggestions(searchQuery);
        makes.slice(0, 8).forEach(make => {
          results.push({
            type: 'make',
            data: make,
            context: make.popular ? 'Popular Brand' : 'Available Brand'
          });
        });
      }
      
      if (selectedMake) {
        // Search for models of the selected make
        // Extract model query by removing the make name prefix
        const makePrefix = selectedMake.name + ' ';
        const modelQuery = searchQuery.startsWith(makePrefix)
          ? searchQuery.slice(makePrefix.length)
          : searchQuery;

        // Search for models of the selected make
        const models = await getVehicleModelSuggestions(selectedMake.id, undefined, modelQuery);
        
        models.slice(0, 10).forEach(model => {
          results.push({
            type: 'model',
            data: model,
            context: `${model.year_from || '?'} - ${model.year_to || 'Present'}`
          });
        });
      }

      // Sort by popularity and relevance
      results.sort((a, b) => getPopularityScore(b.data) - getPopularityScore(a.data));
      
      setSuggestions(results);
      const newIsOpen = results.length > 0;
      setIsOpen(newIsOpen);
      setFocusedIndex(-1);
      onDropdownStateChange?.(newIsOpen);
    } catch (error) {
      console.error('Search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debouncing
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchStep, selectedMake]);

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: VehicleSuggestion) => {
    if (suggestion.type === 'make') {
      const make = suggestion.data as VehicleMake;
      setSelectedMake(make);
      setQuery(make.name + ' ');
      setSearchStep('model');
      setSuggestions([]);
      setIsOpen(false);
      onDropdownStateChange?.(false);
      
      // Auto-trigger model search
      setTimeout(() => {
        setQuery(make.name + ' ');
        inputRef.current?.focus();
      }, 100);
    } else {
      const model = suggestion.data as VehicleModel;
      if (selectedMake) {
        onVehicleSelect({ make: selectedMake, model });
        setQuery(`${selectedMake.name} ${model.name}`);
        setSearchStep('complete');
        const newIsOpen = false;
        setIsOpen(newIsOpen);
        onDropdownStateChange?.(newIsOpen);
      }
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[focusedIndex]);
        }
        break;
      case 'Escape':
        const newIsOpen = false;
        setIsOpen(newIsOpen);
        setFocusedIndex(-1);
        onDropdownStateChange?.(newIsOpen);
        break;
    }
  };

  // Clear search and start over
  const handleClear = () => {
    setQuery('');
    setSelectedMake(null);
    setSearchStep('make');
    setSuggestions([]);
    const newIsOpen = false;
    setIsOpen(newIsOpen);
    onDropdownStateChange?.(newIsOpen);
    inputRef.current?.focus();
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const newIsOpen = false;
        setIsOpen(newIsOpen);
        setFocusedIndex(-1);
        onDropdownStateChange?.(newIsOpen);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Main Search Input */}
      <div className="relative">
        <div className={`relative flex items-center bg-white border-2 rounded-2xl transition-all duration-200 ${
          isOpen ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'
        }`}>
          {/* Search Icon */}
          <div className="absolute left-4 text-gray-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Input Field */}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length > 0 && performSearch(query)}
            placeholder={
              searchStep === 'make' 
                ? "Search for vehicle make (e.g., Toyota, BMW, Audi)"
                : selectedMake 
                ? `Search for ${selectedMake.name} models`
                : "Search for your vehicle"
            }
            className="w-full pl-12 pr-12 py-4 text-lg text-gray-900 bg-transparent focus:outline-none placeholder-gray-400"
            autoComplete="off"
          />

          {/* Loading Spinner */}
          {isLoading && (
            <div className="absolute right-12">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* Clear Button */}
          {query && (
            <button
              onClick={handleClear}
              className="absolute right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Search Progress Indicator */}
        {selectedMake && (
          <div className="absolute top-full left-0 right-0 mt-2">
            <div className="flex items-center space-x-2 px-4">
              <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1.5 rounded-full text-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  {logoErrors.has(selectedMake.name) ? (
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  ) : (
                    <img 
                      src={getManufacturerLogoUrl(selectedMake.name)}
                      alt={`${selectedMake.name} logo`}
                      className="w-5 h-5 object-contain"
                      onError={() => handleLogoError(selectedMake.name)}
                    />
                  )}
                </div>
                <span className="text-blue-600 font-medium">{selectedMake.name}</span>
                <button
                  onClick={() => {
                    setSelectedMake(null);
                    setSearchStep('make');
                    setQuery('');
                  }}
                  className="text-blue-400 hover:text-blue-600"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <span className="text-gray-400 text-sm">→ Select Model</span>
            </div>
          </div>
        )}
      </div>

      {/* Suggestions Dropdown */}
      <AnimatePresence>
        {isOpen && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-[9999] overflow-hidden"
          >
            <div className="max-h-96 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <motion.div
                  key={`${suggestion.type}-${suggestion.data.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.1, delay: index * 0.02 }}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className={`p-4 cursor-pointer transition-all duration-150 ${
                    index === focusedIndex
                      ? 'bg-blue-50 border-l-4 border-blue-500'
                      : 'hover:bg-gray-50'
                  } ${index !== suggestions.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Vehicle Icon/Logo */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center p-2 bg-white border-2 border-gray-200">
                      <ManufacturerLogo 
                        brandName={suggestion.type === 'make' ? suggestion.data.name : selectedMake?.name || ''}
                      />
                    </div>

                    {/* Vehicle Details */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-gray-900">
                          {suggestion.data.name}
                        </span>
                        
                        {/* Type Badge */}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          suggestion.type === 'make'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {suggestion.type === 'make' ? 'Brand' : 'Model'}
                        </span>

                        {/* Popular Badge */}
                        {'popular' in suggestion.data && suggestion.data.popular && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            ⭐ Popular
                          </span>
                        )}
                      </div>

                      {/* Context Information */}
                      <div className="text-sm text-gray-500 mt-1">
                        {suggestion.context}
                        {'default_tyre_size' in suggestion.data && suggestion.data.default_tyre_size && (
                          <span className="ml-2 text-blue-600 font-medium">
                            • {suggestion.data.default_tyre_size}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Arrow Icon */}
                    <div className="text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
              <div className="text-xs text-gray-500 text-center">
                {searchStep === 'make' 
                  ? `${suggestions.length} brands found • Type to refine`
                  : `${suggestions.length} models found • Select to continue`
                }
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No Results */}
      <AnimatePresence>
        {isOpen && !isLoading && suggestions.length === 0 && query.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-[9999] p-8 text-center"
          >
            <div className="text-gray-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="text-gray-600 font-medium">No vehicles found</p>
            <p className="text-sm text-gray-500 mt-1">
              Try a different {searchStep === 'make' ? 'brand name' : 'model name'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernVehicleSearch;